import { useContext, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { LightbulbTip, RemoveUser, AlertErrorCircle, OverflowMenu } from '@/assets/icons'
import { ClientOrganizationUserRead } from '../schemas'
import { AuthContext } from '@/context/auth-context'
import OrgAdminConfirmationDialog, { OrgAdminConfirmationDialogProps } from './OrgAdminConfirmationDialog'
import { useChangeRoleToAdminMutation, useChangeRoleToUserMutation, useRemoveUserFromOrgMutation } from '../apis/org-admin.api'
import { GlobalToastType, kProduction, kToastDurationMediumMs } from '@/constants/constants-ui'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { UserRole } from '../schemas'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem, type MenuItemProps } from '@/components/menu/MenuItem'

type UserListItemMenuOption = Pick<MenuItemProps, 'label' | 'icon' | 'variant' | 'onSelect'>

type Props = {
  userListItem: ClientOrganizationUserRead
  refreshData: () => void
}

const UserListItemMenu: React.FC<Props> = (props: Props) => {
  const { userListItem, refreshData } = props
  const { trackEvent } = useAnalytics()
  const dispatch = useAppDispatch()

  // Auth Context
  const { user: currentUser, userAccountData } = useContext(AuthContext)
  const orgName = userAccountData?.orgData?.name ?? 'organization'

  // Dialog state
  const [openConfirmationDialogProps, setOpenConfirmationDialogProps] = useState<OrgAdminConfirmationDialogProps | null>(null)

  /// RTK-Query Mutations
  const [removeUserFromOrg] = useRemoveUserFromOrgMutation()
  const [changeRoleToAdmin] = useChangeRoleToAdminMutation()
  const [changeRoleToUser] = useChangeRoleToUserMutation()

  const targetUserIsOrgPayer = userListItem.is_organization_payer
  const targetUserIsCurrentUser = userListItem.firebase_id === currentUser?.uid

  const menuOptions: Array<UserListItemMenuOption> = []

  // Any user can be promoted to admin
  if (userListItem.role === UserRole.USER) {
    menuOptions.push({
      label: 'Change role to admin',
      icon: LightbulbTip,
      onSelect: () => {
        setOpenConfirmationDialogProps({
          title: 'Change role to admin',
          message: `Are you sure you want to change ${userListItem.email}'s role to admin?`,
          visible: true,
          onConfirm: async () => {
            await changeRoleToAdmin(userListItem.id).unwrap()
            refreshData()
            dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Role updated to Admin`, durationMs: kToastDurationMediumMs }))
          },
          closeDialog: () => setOpenConfirmationDialogProps(null),
          refreshData: refreshData,
        })
      },
    })
  }

  // Only non-payer admins can be demoted to user
  if (userListItem.role === UserRole.ADMIN && !targetUserIsOrgPayer) {
    menuOptions.push({
      label: 'Change role to user',
      icon: LightbulbTip,
      onSelect: () => {
        setOpenConfirmationDialogProps({
          title: 'Change role to user',
          message: `Are you sure you want to change ${userListItem.email}'s role to user?`,
          visible: true,
          onConfirm: async () => {
            await changeRoleToUser(userListItem.id).unwrap()
            refreshData()
            dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Role updated to User`, durationMs: kToastDurationMediumMs }))
          },
          closeDialog: () => setOpenConfirmationDialogProps(null),
          refreshData: refreshData,
        })
      },
    })
  }

  // Only non-payers can be removed from the org
  if (!targetUserIsOrgPayer) {
    menuOptions.push({
      label: 'Remove',
      icon: RemoveUser,
      variant: 'destructive',
      onSelect: () => {
        setOpenConfirmationDialogProps({
          title: 'Remove member',
          message: `Are you sure you want to remove ${userListItem.email}?`,
          visible: true,
          destructive: true,
          onConfirm: async () => {
            await removeUserFromOrg(userListItem.id).unwrap()
            trackEvent(AnalyticsEvent.OrganizationUserRemoved, {
              organizationId: userListItem.organization_id ?? '',
              organizationUserEmail: userListItem.email,
            })
            refreshData()
            dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `User removed from ${orgName}`, durationMs: kToastDurationMediumMs }))
          },
          closeDialog: () => setOpenConfirmationDialogProps(null),
          refreshData: refreshData,
        })
      },
    })
  }

  // Dev menu items
  if (!kProduction) {
    menuOptions.push({
      label: 'Dev: Log user',
      icon: AlertErrorCircle,
      onSelect: () => {
        console.log('user', userListItem)
      },
    })
  }

  // Hide the menu if the target user is the current user
  if (targetUserIsCurrentUser) {
    return <div className="w-7 h-7" />
  }

  return (
    <>
      {openConfirmationDialogProps && <OrgAdminConfirmationDialog {...openConfirmationDialogProps} />}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          disabled={menuOptions.length === 0}
          className="p-1 rounded-full text-brand-neutral-700 hocus:bg-brand-neutral-200 focus:outline-none data-[state=open]:bg-brand-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <OverflowMenu className="w-5 h-5" />
        </DropdownMenu.Trigger>

        <MenuContent className="min-w-[200px]" align="end" collisionPadding={8}>
          <MenuItemGroup>
            {menuOptions.map((item) => (
              <MenuItem key={item.label} label={item.label} icon={item.icon} variant={item.variant} onSelect={item.onSelect} />
            ))}
          </MenuItemGroup>
        </MenuContent>
      </DropdownMenu.Root>
    </>
  )
}

export default UserListItemMenu
